import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Footer } from "./Footer";
import { Header } from "./Header";
import { PageMeta } from "./PageMeta";
import { buildClasses } from "../utils/buildClasses";
import { FeatureTour } from "./FeatureTour";
import { SignUpAction } from "./SignUpAction";

// font-size inherit: index.css sets a base size directly on spans, which would
// otherwise shrink the period to body size.
function PurplePeriod(): JSX.Element {
  return (
    <span className={buildClasses("text-purple", "[font-size:inherit]")}>
      .
    </span>
  );
}

// One piece of the hero's staggered load-in. Pieces play in order of delay: the
// headline, then the subtitle, then the button, and finally the app mock.
//
// Wrappers, not the pieces themselves, carry the motion: the subtitle's width
// and the Get Started button's position are both measured, and a transform on
// the measured element would skew those readings while it plays.
//
// Reduced motion turns the animations off, which leaves everything in its
// final, visible state.
//
// Prerendering note: the markup is complete from the first render; only the
// visual state starts hidden, so crawlers read all of it either way.
const REVEAL_DELAY_MS = {
  subtitle: 100,
  button: 200,
  mock: 320,
};

function Reveal(props: {
  delayMs: number;
  // blur-up for text and the button; the mock just fades, because a transform
  // left on an ancestor of the tour's sticky mock would change how it's laid
  // out.
  effect?: "blur-up" | "fade-in";
  display?: "inline-block" | "block";
  children: React.ReactNode;
}): JSX.Element {
  const Tag = props.display === "block" ? "div" : "span";
  return (
    <Tag
      className={buildClasses(
        {
          if: props.display === "block",
          then: "block",
          else: buildClasses("inline-block", "[font-size:inherit]"),
        },
        {
          if: props.effect === "fade-in",
          then: "animate-fade-in",
          else: "animate-blur-up",
        },
        "motion-reduce:animate-none",
      )}
      style={{ animationDelay: `${props.delayMs}ms` }}
    >
      {props.children}
    </Tag>
  );
}

// Sizes the subtitle so its single line is exactly as wide as the headline's
// text. Widths depend on the rendered font, so this is measured rather than set
// in CSS.
//
// Once the window is too narrow for the headline to fit on one line, matching
// widths stops working: the subtitle would have to span the whole window, edge
// to edge. So then this returns null, and the subtitle falls back to its classes
// and wraps.
const SUBTITLE_MIN_FONT_PX = 16;
const SUBTITLE_MAX_FONT_PX = 40;

function useFontSizeToMatchWidth(
  targetRef: React.RefObject<HTMLElement>,
  textRef: React.RefObject<HTMLElement>,
): number | null {
  const [fontSize, setFontSize] = useState<number | null>(null);

  useLayoutEffect(() => {
    const target = targetRef.current;
    const text = textRef.current;
    if (target === null || text === null) return;

    const measure = () => {
      // The headline's text, line by line. Ranges over its text nodes measure
      // the glyphs, not the boxes they sit in, which can be as wide as the
      // headline itself. A line can span several text nodes (the purple period
      // is its own), so rects whose vertical middles fall inside one another
      // are merged into one line.
      const lines: {
        top: number;
        bottom: number;
        left: number;
        right: number;
      }[] = [];
      const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
      for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const range = document.createRange();
        range.selectNodeContents(node);
        for (const rect of Array.from(range.getClientRects())) {
          if (rect.width === 0) continue;
          const middle = rect.top + rect.height / 2;
          const line = lines.find(
            (candidate) => middle > candidate.top && middle < candidate.bottom,
          );
          if (line === undefined) {
            lines.push({
              top: rect.top,
              bottom: rect.bottom,
              left: rect.left,
              right: rect.right,
            });
          } else {
            line.left = Math.min(line.left, rect.left);
            line.right = Math.max(line.right, rect.right);
          }
        }
      }
      if (lines.length > 1) {
        setFontSize(null);
        return;
      }

      const targetWidth =
        lines.length === 0 ? 0 : lines[0].right - lines[0].left;
      const textWidth = text.getBoundingClientRect().width;
      const currentSize = parseFloat(getComputedStyle(text).fontSize);
      if (targetWidth === 0 || textWidth === 0) return;

      const next = Math.min(
        SUBTITLE_MAX_FONT_PX,
        Math.max(SUBTITLE_MIN_FONT_PX, (currentSize * targetWidth) / textWidth),
      );
      // Ignore sub-pixel differences so a resize observer can't loop.
      setFontSize((previous) =>
        previous !== null && Math.abs(previous - next) < 0.1 ? previous : next,
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(target);
    // Text metrics shift once the web font replaces the fallback.
    document.fonts?.ready.then(measure).catch(() => {});
    return () => observer.disconnect();
  }, [targetRef, textRef]);

  return fontSize;
}

export function LandingPage() {
  // Note the block body: an expression-bodied effect would return whatever
  // window.scrollTo() evaluates to, and React treats an effect's return value as
  // its cleanup function. That threw "destroy is not a function" on unmount.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getStartedRef = useRef<HTMLDivElement>(null);
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLSpanElement>(null);
  const subtitleFontSize = useFontSizeToMatchWidth(headlineRef, subtitleRef);

  return (
    <div
      className={buildClasses("website-page", "w-full", "bg-base", "isolate")}
    >
      <PageMeta
        title="Bread"
        description="Practical tools for understanding your money."
        path="/"
      />
      <Header />

      {/* Hero */}
      {/* overflow-x-clip lets the tour's mock run off the right edge of the
          window without a horizontal scrollbar. It must be clip, not hidden:
          hidden makes this a scroll container, which breaks the sticky mock. */}
      <section className={buildClasses("w-full", "overflow-x-clip", "pb-20")}>
        <div
          className={buildClasses(
            "mx-auto",
            "w-[92%]",
            "max-w-[1344px]",
            "pt-16",
            "md:pt-24",
          )}
        >
          <h1
            ref={headlineRef}
            className={buildClasses(
              "text-6xl",
              "md:text-8xl",
              "font-semibold",
              "tracking-tight",
              "text-center",
              "text-on-surface-800",
            )}
          >
            <Reveal delayMs={0}>
              Know your dough
              <PurplePeriod />
            </Reveal>
          </h1>
          <p
            className={buildClasses(
              "mt-6",
              // On the paragraph, not the text's own span: balancing applies to
              // the block the lines wrap in, which here is the Reveal wrapper
              // between them, and it inherits from here.
              "text-balance",
              "text-center",
              "font-light",
              "leading-relaxed",
              "text-on-surface-600",
            )}
          >
            {/* One unbroken line, sized to match the headline's width. Until
                the first measurement, which the prerendered HTML is shown
                before, the sm and md classes hold the sizes it measures at each
                headline size, where they fit on one line by themselves. If
                either line's copy changes, re-measure them, or the subtitle
                jumps when the app takes over. Wherever the headline wraps, the
                subtitle is left to wrap too. */}
            <Reveal delayMs={REVEAL_DELAY_MS.subtitle}>
              <span
                ref={subtitleRef}
                className={buildClasses(
                  "text-xl",
                  "sm:text-[1.463rem]",
                  "md:text-[2.466rem]",
                )}
                style={
                  subtitleFontSize === null
                    ? undefined
                    : { fontSize: subtitleFontSize, whiteSpace: "nowrap" }
                }
              >
                Practical tools for understanding your money.
              </span>
            </Reveal>
          </p>

          <div
            ref={getStartedRef}
            className={buildClasses("mx-auto", "mt-10", "w-fit")}
          >
            <Reveal delayMs={REVEAL_DELAY_MS.button}>
              <SignUpAction label="Get started" mobileNote={false} />
            </Reveal>
          </div>

          <div className={buildClasses("mt-16")}>
            <Reveal
              delayMs={REVEAL_DELAY_MS.mock}
              effect="fade-in"
              display="block"
            >
              <FeatureTour dockAfterRef={getStartedRef} />
            </Reveal>
          </div>
        </div>
      </section>

      <section
        aria-label="Pricing and signup"
        className={buildClasses(
          "mx-auto",
          "w-[92%]",
          "max-w-[1344px]",
          "mb-24",
          "rounded-3xl",
          "bg-purple-100",
          "px-6",
          "py-16",
          "md:py-24",
          "text-center",
        )}
      >
        <h2
          className={buildClasses(
            "text-3xl",
            "md:text-4xl",
            "font-normal",
            "tracking-tight",
            "text-on-surface-800",
          )}
        >
          Only $3 per month.
        </h2>
        <div className={buildClasses("mt-10", "flex", "justify-center")}>
          <SignUpAction label="Get started" arrow />
        </div>
      </section>

      <Footer />
    </div>
  );
}
